import React, { useState, useEffect } from 'react';
import config from '../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';

const ViewUsers = ({ testMode = false }) => {
    
  // const host = testMode ? config.testBackendUrl : config.backendUrl;
  const host = config.backendUrl;

  const [users, setUsers] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getAuthToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        setAuthToken(token);
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    };
    
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!authToken) return; // Don't fetch if no token yet

    setLoading(true);
    fetch(`${host}/api/admin/users`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(data => {
        console.log('Fetched users:', data);
        setUsers(data);
        setLoading(false);
  })
    .catch(error => {
      console.error('Error fetching users:', error);
      setLoading(false);
    });
  }, [authToken, refresh]);

  const adminUsers = users.filter(user => user.Groups.length > 0);
  const regularUsers = users.filter(user => user.Groups.length === 0);

  const getUserName = (user) => {
    const givenName = user.Attributes.find(attr => attr.Name === 'given_name')?.Value || '';
    const familyName = user.Attributes.find(attr => attr.Name === 'family_name')?.Value || '';
    return `${givenName} ${familyName}`.trim() || 'No name provided';
  };

  const getUserEmail = (user) => {
    return user.Attributes[0]?.Value || 'No email provided';
  };

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            Manage Users
            {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
          </h1>
        </div>
        <div className="page-header-actions">
          <Link to={testMode ? "/test" : "/"} className="btn btn-secondary">
            ← Back Home
          </Link>
          <button 
            className="btn btn-ghost" 
            onClick={() => {
              window.location.href = testMode ? '/users' : '/test/users';
              setRefresh(!refresh);
            }}
          >
            🔄 Toggle Mode
          </button>
        </div>
      </div>

      {/* User Summary Cards */}
      <div className="dashboard-grid" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h3 className="dashboard-card-title">Total Users</h3>
            <span>👥</span>
          </div>
          <p className="dashboard-card-value">{users.length}</p>
          <div className="dashboard-card-change">
            <span>All registered users</span>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h3 className="dashboard-card-title">Admin Users</h3>
            <span>👑</span>
          </div>
          <p className="dashboard-card-value">{adminUsers.length}</p>
          <div className="dashboard-card-change positive">
            <span>↗</span>
            <span>Administrators</span>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h3 className="dashboard-card-title">Regular Users</h3>
            <span>👤</span>
          </div>
          <p className="dashboard-card-value">{regularUsers.length}</p>
          <div className="dashboard-card-change">
            <span>Standard users</span>
          </div>
        </div>
      </div>

      {/* Admin Users Table */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Admin Users ({adminUsers.length})</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
              <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
                Loading users...
              </p>
            </div>
          ) : adminUsers.length > 0 ? (
            <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Email</th>
                    <th>Full Name</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map(user => (
                    <tr key={user.Username}>
                      <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                        {user.Username}
                      </td>
                      <td>{getUserEmail(user)}</td>
                      <td style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                        {getUserName(user)}
                      </td>
                      <td>
                        <span className="badge badge-success">Admin</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>👑</div>
              <h3 style={{ marginBottom: 'var(--spacing-md)' }}>No Admin Users</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Admin users will appear here once they are assigned admin privileges.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Regular Users Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Regular Users ({regularUsers.length})</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
              <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
                Loading users...
              </p>
            </div>
          ) : regularUsers.length > 0 ? (
            <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Email</th>
                    <th>Full Name</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {regularUsers.map(user => (
                    <tr key={user.Username}>
                      <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                        {user.Username}
                      </td>
                      <td>{getUserEmail(user)}</td>
                      <td style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                        {getUserName(user)}
                      </td>
                      <td>
                        <span className="badge badge-secondary">User</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>👤</div>
              <h3 style={{ marginBottom: 'var(--spacing-md)' }}>No Regular Users</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Regular users will appear here once they register.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewUsers;